import { useEffect } from 'react';
import { useParams, useNavigate, Navigate, Link } from 'react-router';
import { motion } from 'framer-motion';
import { allHeroes } from '../../../game/data/heroes';
import { getHeroBooster } from '../../../game/data/cards';
import { useMatchStore, MAX_PICKS } from '../../../stores/match';
import { CardTile } from '../../../components/CardTile';
import { WordMark } from '../../../components/WordMark';
import { useMotionTier } from '../../../components/useMotionTier';
import { useLayoutMode } from '../../../components/useLayoutMode';
import { useTransitionStore, registerSlot } from '../../../stores/transition';
import { duration, ease, stagger } from '../../../theme/motion';
import { sfx } from '../../../audio/sfx';

// R4 — Starter Draft. Generates a 6-card booster from
// getHeroBooster(heroId), persists it on the match store so remounts
// don't reshuffle, lets the player pick exactly MAX_PICKS (3), then
// hands off to the Tavern route.

export function DraftPage() {
  const { matchId: urlMatchId } = useParams<{ matchId: string }>();
  const { mode } = useLayoutMode();
  const navigate = useNavigate();
  const tier = useMotionTier();
  const reducedMotion = tier === 'reduced';
  const startTransition = useTransitionStore((s) => s.start);
  const storedMatchId = useMatchStore((s) => s.matchId);
  const heroId = useMatchStore((s) => s.heroId);
  const booster = useMatchStore((s) => s.booster);
  const picks = useMatchStore((s) => s.picks);
  const setBooster = useMatchStore((s) => s.setBooster);
  const togglePick = useMatchStore((s) => s.togglePick);
  const confirmDraft = useMatchStore((s) => s.confirmDraft);

  // Generate booster once per match when we land here with a hero set.
  // Re-renders read from store; only an empty booster triggers generation.
  useEffect(() => {
    if (heroId && booster.length === 0) {
      setBooster(getHeroBooster(heroId, 6));
    }
  }, [heroId, booster.length, setBooster]);

  if (!urlMatchId || storedMatchId !== urlMatchId || !heroId) {
    return <Navigate to="/play" replace />;
  }

  const hero = allHeroes.find((h) => h.id === heroId);
  const heroStarterCount = booster.filter((c) => c.starterForHeroId === heroId).length;
  const neutralCount = booster.length - heroStarterCount;
  const canConfirm = picks.length === MAX_PICKS;

  function confirm() {
    if (!canConfirm) return;
    const go = () => {
      confirmDraft();
      navigate(`/game/${urlMatchId}/tavern`);
    };
    const started = startTransition({
      kind: 'advance',
      commit: go,
      payload: { carry: 'Tavern' },
    });
    if (!started) go();
  }

  return (
    <main className="pt-safe relative h-dvh w-full overflow-hidden bg-asphalt">
      <div
        aria-hidden
        className="pointer-events-none absolute -right-1/4 -top-1/4 h-[50vw] w-[50vw] max-h-[640px] max-w-[640px]"
        style={{
          background:
            'radial-gradient(circle, rgba(255,138,61,0.15) 0%, rgba(255,138,61,0) 70%)',
        }}
      />

      <section className="relative mx-auto flex h-full w-full max-w-6xl flex-col gap-sm overflow-y-auto px-md py-sm md:gap-md md:px-lg md:py-md lg:gap-lg lg:px-xl lg:py-lg">
        <header className="flex items-center justify-between">
          <span className="md:hidden">
            <WordMark size="sm" />
          </span>
          <span className="hidden md:inline lg:hidden">
            <WordMark size="md" />
          </span>
          <span className="hidden lg:inline">
            <WordMark size="lg" />
          </span>
          <Link
            to={`/game/${urlMatchId}/hero`}
            className="font-data text-[10px] uppercase tracking-[0.3em] text-graphite hover:text-marker md:text-[12px] lg:text-[14px]"
          >
            ← hero
          </Link>
        </header>

        <motion.div
          initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={
            reducedMotion
              ? { duration: duration.tap }
              : { duration: duration.enter, ease: ease.out }
          }
        >
          <p className="font-data text-[10px] uppercase tracking-[0.3em] text-marker md:text-[12px] lg:text-[14px]">
            step 2 of 4 · starter draft
          </p>
          <h1 className="mt-xs font-cholo text-[24px] leading-none text-sodium md:text-[40px] lg:text-[56px]">
            Pick three
          </h1>
          {hero && (
            <p className="mt-xs hidden text-[14px] text-marker md:block md:text-[16px] lg:text-[18px]">
              {/* Landing slot for the reel advance out of hero select: the
                  chosen hero's name travels from the card they picked to here. */}
              <span ref={registerSlot('to')} className="text-bone">
                {hero.name}
              </span>
              's pack —{' '}
              <span className="text-sodium">{heroStarterCount}</span> hero starter
              {heroStarterCount === 1 ? '' : 's'},{' '}
              <span className="text-bone">{neutralCount}</span> neutral
              {neutralCount === 1 ? '' : 's'}. Hero cards have a sodium glow.
            </p>
          )}
        </motion.div>

        {/* The draft is a READING screen — you cannot pick well from cards
            whose rules text is truncated. A 6-wide row at 390px gave ~52px
            cards with the text cut mid-sentence, so portrait drops to 2
            columns (~170px cards, full text) and pays for it with the
            vertical space the section already scrolls into. Landscape uses 3
            (it has width but no height); desktop keeps the single row. */}
        <div
          className={`grid gap-xs md:gap-md lg:gap-lg ${
            mode === 'phonePortrait'
              ? 'grid-cols-2'
              : mode === 'phoneLandscape'
                ? 'grid-cols-3'
                : 'grid-cols-3 lg:grid-cols-6'
          }`}
        >
          {booster.map((card, i) => {
            const selected = picks.some((p) => p.id === card.id);
            const atCap = picks.length >= MAX_PICKS && !selected;
            return (
              <motion.div
                key={card.id}
                initial={
                  reducedMotion
                    ? { opacity: 0 }
                    : { opacity: 0, y: 16, rotate: i % 2 === 0 ? -2.5 : 2.5 }
                }
                animate={{ opacity: 1, y: 0, rotate: 0 }}
                transition={
                  reducedMotion
                    ? { duration: duration.tap }
                    : {
                        delay: stagger.tight * i,
                        duration: duration.enter,
                        ease: ease.out,
                      }
                }
              >
                <CardTile
                  card={card}
                  selected={selected}
                  starter={card.starterForHeroId === heroId}
                  onClick={() => {
                    sfx.click();
                    togglePick(card);
                  }}
                  disabled={atCap}
                />
              </motion.div>
            );
          })}
        </div>

        {/* Confirm bar — sticks to the bottom edge so it stays reachable
            once the 2-column portrait grid makes the section scroll. */}
        <div className="pb-safe sticky bottom-0 z-10 mt-auto flex items-center justify-between gap-sm bg-asphalt/95 pt-xs backdrop-blur-sm md:gap-lg md:pt-md">
          <p className="font-data text-[11px] uppercase tracking-[0.3em] text-marker md:text-[12px] lg:text-[14px]">
            picked{' '}
            <span className="text-sodium">{picks.length}</span>
            <span className="text-graphite"> / {MAX_PICKS}</span>
          </p>
          <motion.button
            onClick={confirm}
            disabled={!canConfirm}
            whileHover={canConfirm && !reducedMotion ? { scale: 1.02 } : undefined}
            whileTap={canConfirm && !reducedMotion ? { scale: 0.97 } : undefined}
            className={`inline-block min-h-[44px] rounded-sm px-md py-sm font-data text-[13px] uppercase tracking-[0.2em] transition-colors md:min-h-[52px] md:px-xl md:py-md md:text-[16px] lg:min-h-[64px] lg:px-2xl lg:py-lg lg:text-[20px] ${
              canConfirm
                ? 'bg-sodium text-sharpie shadow-sodium hover:bg-sodiumGlow'
                : 'cursor-not-allowed bg-stucco2 text-graphite'
            }`}
          >
            Open tavern ›
          </motion.button>
        </div>
      </section>
    </main>
  );
}
